import DOMPurify from 'dompurify';
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { TeaPubSub } from '../tea-pub-sub.ts';

/**
 * We need a handle to unsubscribe from the event listener during unmount.
 */
let clickHandlerHandle: ((event: MouseEvent) => void) | undefined = undefined;

export const ReadOnlyEditor = ({
  html,
  documentUuid,
}: {
  html: string;
  documentUuid: string;
}) => {
  if (typeof html === 'undefined') return null;

  const sanitizedHTML = DOMPurify.sanitize(html, {
    ADD_ATTR: ['href', 'target'], // required for anchor tags
  });

  const containerDivRef = useRef<HTMLDivElement | null>(null);

  clickHandlerHandle = (clickEvent) => {
    if (!(clickEvent.target instanceof HTMLElement)) return;

    TeaPubSub.instance.publish('element-clicked', {
      id: documentUuid,
      element: clickEvent.target,
    });
  };

  useEffect(() => {
    if (containerDivRef.current === null) return;

    if (clickHandlerHandle !== undefined) {
      containerDivRef.current.addEventListener('click', clickHandlerHandle);
    }

    /**
     * If there's an url hash, scroll this element into view then clear it from
     * the address bar.
     */
    const url = new URL(window.location.href);
    if (url.hash) {
      const elementId = url.hash.replace('#', '');
      const element = containerDivRef.current?.querySelector(`#\\${elementId}`);
      if (element) {
        element.scrollIntoView();
        url.hash = '';
        window.history.replaceState(null, '', url);
      }
    }

    return () => {
      if (containerDivRef.current === null) return;

      if (clickHandlerHandle !== undefined) {
        containerDivRef.current.removeEventListener(
          'click',
          clickHandlerHandle,
        );
      }
    };
  }, []);

  useAnchoredHeadingsAccessibilityHandlers(containerDivRef);

  return (
    <div
      ref={containerDivRef}
      className="tea-read-only"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: the HTML will be sanitized
      dangerouslySetInnerHTML={{
        __html: sanitizedHTML,
      }}
    />
  );
};

const useAnchoredHeadingsAccessibilityHandlers = (
  containerDivRef: RefObject<HTMLDivElement | null>,
) => {
  useEffect(() => {
    if (!containerDivRef.current) return;

    const anchoredHeadings =
      containerDivRef.current.querySelectorAll('[data-anchored]');
    const onMouseDownHandler = (event: MouseEvent) => {
      const heading = event.target as HTMLElement | null;
      heading?.setAttribute('aria-pressed', 'true');
    };
    const onMouseUpHandler = (event: MouseEvent) => {
      const heading = event.target as HTMLElement | null;
      heading?.setAttribute('aria-pressed', 'false');
    };
    const onKeyDownHandler = (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;

      const heading = event.target as HTMLElement | null;
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      heading?.dispatchEvent(clickEvent);
      heading?.setAttribute('aria-pressed', 'true');
    };
    const onKeyUpHandler = (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;

      const heading = event.target as HTMLElement | null;
      heading?.setAttribute('aria-pressed', 'false');
    };

    for (const headingIndex in anchoredHeadings) {
      const heading = anchoredHeadings[headingIndex];
      if (heading instanceof HTMLElement) {
        heading.addEventListener('mousedown', onMouseDownHandler);
        heading.addEventListener('mouseup', onMouseUpHandler);
        heading.addEventListener('keydown', onKeyDownHandler);
        heading.addEventListener('keyup', onKeyUpHandler);
      }
    }

    return () => {
      for (const headingIndex in anchoredHeadings) {
        const heading = anchoredHeadings[headingIndex];
        if (heading instanceof HTMLElement) {
          heading.removeEventListener('mousedown', onMouseDownHandler);
          heading.removeEventListener('mouseup', onMouseUpHandler);
          heading.removeEventListener('keydown', onKeyDownHandler);
          heading.removeEventListener('keyup', onKeyUpHandler);
        }
      }
    };
  }, [containerDivRef.current]);
};
